import * as Sentry from '@sentry/angular';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {AuthenticateApi} from '../api/authenticate.api';
import {BehaviorSubject, Observable, Subscription, timer} from 'rxjs';
import {Session} from '../objects/session';
import {NgxPermissionsService} from 'ngx-permissions';
import {ToastrService} from 'ngx-toastr';
import {User} from '../objects/user';
import {Administration} from '@shared/objects/administration';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {ConfigUtil} from '@shared/services/config.util';

@Injectable({
  providedIn: 'root'
})
export class SessionService implements OnDestroy {
  private readonly authenticateApi = inject(AuthenticateApi);
  private readonly oauthService = inject(OAuthService);
  private readonly permissionService = inject(NgxPermissionsService);
  private readonly toastrService = inject(ToastrService);

  private _session: BehaviorSubject<Session>;
  private subscriptions = new Subscription();

  constructor() {
    this._session = new BehaviorSubject<Session>(new Session());

    this.subscriptions.add(
      this.oauthService.events.subscribe((event: OAuthEvent) => {
        switch (event.type) {
          case 'token_expires':
            console.log('token expires');
            break;
          case 'token_received': {
            const isLoggedIn = this.oauthService.hasValidAccessToken();
            if (isLoggedIn) {
              this.updateSession();
            }
            break;
          }
          case 'session_terminated':
          case 'session_error':
            console.warn('Session terminated or error');
            break;
          case 'logout':
            console.log('User logged out');
            break;
          case 'code_error':
            console.error('OAuth error', event);
            break;
        }
      })
    );

    this.subscriptions.add(
      this._session.subscribe(session => {
        if (session && !!ConfigUtil.get('sentry')['enabled']) {
          this.initSentry(session);
        }
      })
    );

    // Initialize if authenticated
    if (this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()) {
      this.subscriptions.add(
        timer(0).subscribe(() => this.updateSession())
      );
    }

  }

  public get session(): Session {
    return this._session.getValue();
  }

  public get session$(): Observable<Session> {
    return this._session.asObservable();
  }

  hasPermission(permission: string | string[]): Promise<boolean> {
    return this.permissionService.hasPermission(permission);
  }


  private updateSession() {
    const claims = this.oauthService.getIdentityClaims();
    this.authenticateApi.getAdministrations().subscribe(administrations => {
      const roles: string[] = claims['realm_access']['roles'];

      if (administrations.length === 0) {
        this.toastrService.error('Deze gebruiker is niet gekoppeld aan een administratie! Neem contact met ons op.', 'Fout!', {
          timeOut: 0,
          extendedTimeOut: 0
        });
        return;
      }

      // Create the new session
      const session = new Session();
      session.user = this.createUser(claims);
      session.loggedIn = !!session.user;
      session.administration = this.session?.administration ? this.session.administration : administrations[0];
      session.availableAdministrations = administrations;
      session.permissions = roles;

      this.permissionService.loadPermissions(roles);

      this._session.next(session);
    });
  }

  public setAdministration(administration: Administration) {
    const session: Session = Object.assign(new Session(), this.session);
    session.administration = administration;
    this._session.next(session);
  }

  private createUser(claims?: Record<string, unknown>): User {
    const user = new User();
    user.username = claims['preferred_username'] as string;
    user.firstName = claims['given_name'] as string;
    user.lastName = claims['family_name'] as string;
    user.email = claims['email'] as string;
    return user;
  }

  /**
   * Add some extra data to Sentry
   * @param session
   */
  private initSentry(session: Session): void {
    if (session.user) {
      Sentry.setUser({
        username: session.user.username,
        name: session.user.firstName + ' ' + session.user.lastName,
        email: session.user.email
      })
    }

    if (session.administration) {
      Sentry.setTag('administration-uuid', String(session.administration.uuid));
      Sentry.setTag('administration', session.administration.name);
    }

    if (session.permissions) {
      Sentry.setExtra('permissions', session.permissions);
    }
  }

  logout() {
    this._session.next(new Session());
    const idToken = this.oauthService.getIdToken();
    this.oauthService.logOut({
      id_token_hint: idToken,
      postLogoutRedirectUri: window.location.origin
    });
    this.permissionService.flushPermissions();
  }

  myAccount() {
    const k = ConfigUtil.get('keycloak');
    window.location.href = `${k['url']}/realms/${k['realm']}/account?referrer=${k['clientId']}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
